import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Slider, Input, Radio, Space, message, Row, Col, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { emotionApi } from '@tradermind/shared/api';
import {
  createEmotionLogSchema,
  type CreateEmotionLogFormData,
} from '@tradermind/shared/validators';
import {
  EMOTION_TYPES,
  EMOTION_TRIGGERS,
  EMOTION_CONTEXTS,
  getIntensityColor,
  getIntensityLabelKey,
} from '@tradermind/shared/constants';

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function EmotionFormPage() {
  const { t } = useTranslation('emotion');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CreateEmotionLogFormData>({
    resolver: zodResolver(createEmotionLogSchema),
    defaultValues: {
      emotion_type: undefined,
      intensity: 5,
      trigger: undefined,
      context: 'before_trade',
      notes: '',
    },
  });

  const intensity = watch('intensity');
  const selectedEmotion = watch('emotion_type');

  const createMutation = useMutation({
    mutationFn: emotionApi.createEmotionLog,
    onSuccess: () => {
      message.success(t('form.success'));
      queryClient.invalidateQueries({ queryKey: ['emotions'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      navigate('/emotions');
    },
    onError: () => {
      message.error(t('form.failed'));
    },
  });

  const onSubmit = (data: CreateEmotionLogFormData) => {
    createMutation.mutate(data);
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            {t('back')}
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {t('form.title')}
          </Title>
        </Space>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Row gutter={24}>
          <Col xs={24} lg={16}>
            <Card title={t('form.how_feeling')} style={{ marginBottom: 24 }}>
              <div style={{ marginBottom: 24 }}>
                <Text strong style={{ display: 'block', marginBottom: 12 }}>
                  {t('form.select_type')}
                </Text>
                <Controller
                  name="emotion_type"
                  control={control}
                  render={({ field }) => (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      {EMOTION_TYPES.map((emotion) => (
                        <div
                          key={emotion.id}
                          onClick={() => field.onChange(emotion.id)}
                          style={{
                            padding: '12px 20px',
                            borderRadius: 8,
                            border: `2px solid ${
                              field.value === emotion.id ? emotion.color : '#e5e7eb'
                            }`,
                            background:
                              field.value === emotion.id ? `${emotion.color}15` : '#fff',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            textAlign: 'center',
                          }}
                        >
                          <div style={{ fontSize: 28 }}>{emotion.emoji}</div>
                          <div style={{ marginTop: 4, fontWeight: 500 }}>{t(emotion.label)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                />
                {errors.emotion_type && (
                  <Text type="danger" style={{ marginTop: 8, display: 'block' }}>
                    {errors.emotion_type.message}
                  </Text>
                )}
              </div>

              <div style={{ marginBottom: 24 }}>
                <Text strong style={{ display: 'block', marginBottom: 12 }}>
                  {t('form.intensity_label')}
                </Text>
                <Controller
                  name="intensity"
                  control={control}
                  render={({ field }) => (
                    <div>
                      <Slider
                        {...field}
                        min={1}
                        max={10}
                        marks={{
                          1: '1',
                          3: '3',
                          5: '5',
                          7: '7',
                          10: '10',
                        }}
                        trackStyle={{ backgroundColor: getIntensityColor(field.value) }}
                        handleStyle={{ borderColor: getIntensityColor(field.value) }}
                      />
                      <div style={{ textAlign: 'center', marginTop: 8 }}>
                        <Text
                          style={{
                            fontSize: 24,
                            fontWeight: 600,
                            color: getIntensityColor(field.value),
                          }}
                        >
                          {field.value}
                        </Text>
                        <Text type="secondary" style={{ marginLeft: 8 }}>
                          ({t(getIntensityLabelKey(field.value))})
                        </Text>
                      </div>
                    </div>
                  )}
                />
              </div>

              <div style={{ marginBottom: 24 }}>
                <Text strong style={{ display: 'block', marginBottom: 12 }}>
                  {t('form.trigger')}
                </Text>
                <Controller
                  name="trigger"
                  control={control}
                  render={({ field }) => (
                    <Radio.Group
                      {...field}
                      optionType="button"
                      buttonStyle="solid"
                      options={EMOTION_TRIGGERS.map((trigger) => ({
                        value: trigger.id,
                        label: t(trigger.label),
                      }))}
                    />
                  )}
                />
              </div>

              <div>
                <Text strong style={{ display: 'block', marginBottom: 12 }}>
                  {t('form.current_context')}
                </Text>
                <Controller
                  name="context"
                  control={control}
                  render={({ field }) => (
                    <Radio.Group
                      {...field}
                      optionType="button"
                      buttonStyle="solid"
                      options={EMOTION_CONTEXTS.map((ctx) => ({
                        value: ctx.value,
                        label: t(ctx.label),
                      }))}
                    />
                  )}
                />
                {errors.context && (
                  <Text type="danger" style={{ marginTop: 8, display: 'block' }}>
                    {errors.context.message}
                  </Text>
                )}
              </div>
            </Card>

            <Card title={t('form.notes')}>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextArea
                    {...field}
                    rows={4}
                    placeholder={t('form.notes_placeholder')}
                    maxLength={500}
                    showCount
                  />
                )}
              />
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card title={t('form.preview')}>
              {selectedEmotion ? (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 64, marginBottom: 16 }}>
                    {EMOTION_TYPES.find((e) => e.id === selectedEmotion)?.emoji}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
                    {t(EMOTION_TYPES.find((e) => e.id === selectedEmotion)?.label || '')}
                  </div>
                  <div
                    style={{
                      fontSize: 32,
                      fontWeight: 700,
                      color: getIntensityColor(intensity),
                    }}
                  >
                    {intensity}/10
                  </div>
                  <div style={{ color: '#999', marginTop: 4 }}>
                    {t(getIntensityLabelKey(intensity))}
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>
                  {t('form.select_emotion_hint')}
                </div>
              )}
            </Card>

            <Card title={t('form.actions')} style={{ marginTop: 24 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="large"
                  loading={createMutation.isPending}
                  disabled={!selectedEmotion}
                >
                  {t('form.submit')}
                </Button>
                <Button block onClick={() => navigate(-1)}>
                  {t('common:cancel')}
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>
      </form>
    </div>
  );
}
